// src/lib/analytics.ts

// Declare gtag function for TypeScript
declare global {
  interface Window {
    dataLayer: any[];
    gtag: (...args: any[]) => void;
  }
}

/**
 * Track custom events in Google Analytics
 */
export const trackEvent = (
  eventName: string,
  eventParams?: {
    [key: string]: string | number | boolean;
  }
) => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("event", eventName, eventParams);
  }
};

/**
 * Track user sign up
 */
export const trackSignUp = (method: "email" = "email") => {
  trackEvent("sign_up", { method });
};

/**
 * Track user login
 */
export const trackLogin = (method: "email" = "email") => {
  trackEvent("login", { method });
};

/**
 * Track squad creation
 */
export const trackSquadCreated = (squadName: string) => {
  trackEvent("squad_created", { squad_name: squadName });
};

/**
 * Track match prediction
 */
export const trackPredictionMade = (
  matchId: string,
  homeTeam: string,
  awayTeam: string
) => {
  trackEvent("prediction_made", {
    match_id: matchId,
    home_team: homeTeam,
    away_team: awayTeam,
  });
};

/**
 * Track image share/download
 */
export const trackImageShared = (type: "prediction" | "story") => {
  trackEvent("image_shared", { image_type: type });
};

/**
 * Track image generation/download
 */
export const trackImageGenerated = (type: "prediction" | "story") => {
  trackEvent("image_generated", { image_type: type });
};

/**
 * Track link clicks
 */
export const trackLinkClick = (linkName: string, linkUrl?: string) => {
  trackEvent("link_click", {
    link_name: linkName,
    link_url: linkUrl || "",
  });
};

/**
 * Track header button clicks
 */
export const trackHeaderClick = (buttonName: string) => {
  trackEvent("header_button_click", { button_name: buttonName });
};

/**
 * Track navigation events
 */
export const trackNavigation = (screen: string) => {
  trackEvent("navigation", { screen });
};

/**
 * Track squad switcher actions
 */
export const trackSquadAction = (
  action:
    | "switch_global"
    | "switch_squad"
    | "create"
    | "join"
    | "leave"
    | "copy_code"
) => {
  trackEvent("squad_action", { action });
};

/**
 * Track language change
 */
export const trackLanguageChange = (language: string) => {
  trackEvent("language_change", { language });
};

/**
 * Track logout
 */
export const trackLogout = () => {
  trackEvent("logout");
};

/**
 * Track ad close
 */
export const trackAdClose = (adType: "fullscreen" | "banner" = "fullscreen") => {
  trackEvent("ad_closed", { ad_type: adType });
};

/**
 * Track first-time modal prediction
 */
export const trackFirstTimePrediction = (
  matchId: string,
  homeTeam: string,
  awayTeam: string,
  choice: "home" | "away" | "draw"
) => {
  trackEvent("first_time_prediction_made", {
    match_id: matchId,
    home_team: homeTeam,
    away_team: awayTeam,
    prediction_choice: choice,
    source: "first_time_modal",
  });
};

/**
 * Track first-time modal sign up click
 */
export const trackFirstTimeSignUpClick = (
  matchId: string,
  homeTeam: string,
  awayTeam: string
) => {
  trackEvent("first_time_signup_click", {
    match_id: matchId,
    home_team: homeTeam,
    away_team: awayTeam,
    source: "first_time_modal",
  });
};
